import {
  beraterCartLineProperties,
  publishBeraterAddToCart,
  publishBeraterEmptyResult,
  publishBeraterItemRemoved,
  publishBeraterPdpClick,
  publishBeraterResultViewed,
} from "./analytics";
import { getRecommendations } from "./engine";
import { normalizeAccessoryMode } from "./normalize";
import { calculateShippingCost, formatPrice, getInitialCountry, persistCountry, populateCountrySelect } from "./shipping";
import { openTrustpilotDrawer, renderTrustpilotStars } from "./trustpilot";
import {
  buildDesiredSelection,
  buildSubtitle,
  desiredSelectionWithMainVariant,
  isBundleTokenInVariant,
  resolveAccessoryItem,
  resolveMainVariant,
} from "./variantResolver";
import { resolveVariantImageSrc } from "./variantImage";
import type { BeraterAnswer, BeraterData, BeraterRecommendation, ResolvedItem, ShopifyJsProduct, ShopifyJsVariant } from "./types";

// openCartDrawerGlobal and ProductCardSwatches are declared in
// src/cartDrawer/expose-global.ts / src/initProductCardSwatches.ts
declare global {
  interface Window {
    Swiper: any;
    variantStrings?: { addToCart?: string; soldOut?: string };
  }
}

const WINNER_CARD_SECTION = "ek-berater-result-card";

const formatBeraterInlineText = (text: string): string =>
  text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\s*\|\s*/g, "&nbsp;&nbsp;|&nbsp;&nbsp;");

const DROPPED_LABELS: Record<string, string> = {
  wood: "Holzart",
  bed_type: "Bettart",
  size: "Größe",
};

const fetchSectionHtml = async (handle: string, section: string, query = ""): Promise<string | null> => {
  try {
    const response = await fetch(`/products/${handle}?sections=${section}${query}`);
    if (!response.ok) return null;
    const data = (await response.json()) as Record<string, string>;
    return data[section] ?? null;
  } catch {
    return null;
  }
};

const fetchProductJs = async (handle: string): Promise<ShopifyJsProduct | null> => {
  try {
    const response = await fetch(`/products/${handle}.js`);
    if (!response.ok) return null;
    return (await response.json()) as ShopifyJsProduct;
  } catch {
    return null;
  }
};

const imageWithWidth = (src: string, width: number): string => {
  if (!src) return src;
  const normalized = src.startsWith("//") ? `https:${src}` : src;
  return normalized.includes("?") ? `${normalized}&width=${width}` : `${normalized}?width=${width}`;
};

const patchGalleryHeroImage = (card: HTMLElement, product: ShopifyJsProduct, variant: ShopifyJsVariant): void => {
  const src = resolveVariantImageSrc(product, variant);
  if (!src) return;

  const img = card.querySelector<HTMLImageElement>("[data-berater-gallery] .swiper-slide:first-child img.berater-gallery-img");
  if (!img) return;

  const resolved = imageWithWidth(src, 800);
  if (normalizeGallerySrc(img.currentSrc || img.src) === normalizeGallerySrc(resolved)) return;

  img.src = resolved;
  img.srcset = `${imageWithWidth(src, 400)} 400w, ${resolved} 800w`;
};

const normalizeGallerySrc = (src: string): string => src.replace(/\?.*$/, "").replace(/^https?:/, "");

const productPdpHref = (product: ShopifyJsProduct, variantId?: number): string => {
  const params = new URLSearchParams({ ref: "berater" });
  if (variantId) params.set("variant", String(variantId));
  return `${product.url}?${params.toString()}`;
};

const initGallerySwiper = (card: HTMLElement, attempt = 0): void => {
  const gallery = card.querySelector<HTMLElement>("[data-berater-gallery]");
  if (!gallery || gallery.dataset.swiperInit === "true") return;

  if (typeof window.Swiper !== "function") {
    if (attempt < 20) window.setTimeout(() => initGallerySwiper(card, attempt + 1), 250);
    return;
  }

  gallery.dataset.swiperInit = "true";
  const prevButton = gallery.querySelector<HTMLElement>(".swiper-button-prev");
  const nextButton = gallery.querySelector<HTMLElement>(".swiper-button-next");
  const swiper = new window.Swiper(gallery, {
    slidesPerView: 1,
    spaceBetween: 0,
    watchOverflow: true,
    autoHeight: false,
    observer: true,
    observeParents: true,
    navigation:
      prevButton && nextButton
        ? {
            nextEl: nextButton,
            prevEl: prevButton,
            disabledClass: "swiper-button-disabled",
          }
        : undefined,
    pagination: {
      el: card.querySelector("[data-berater-gallery-dots]"),
      clickable: true,
    },
  });
  swiper.update();
};

const shippingBadgeSvg = (iconClass: string) =>
  `<svg class="absolute left-3 top-1/2 h-3.5 w-3 -translate-y-1/2 shrink-0 ${iconClass}" width="12" height="14" viewBox="0 0 12 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M0.501953 3.08398V9.74922L6.00074 12.7486L11.4995 9.74922V3.08398L6.02372 5.48301L0.501953 3.08398Z" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/><path d="M11.4976 3.02001L6.02176 0.5L0.5 3.02001" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/><path d="M8.7721 4.19439L3.36523 1.73145" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/><path d="M6.00098 5.46973V12.418" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

const createShippingBadge = (placement: "summary" | "line"): HTMLElement => {
  const isLine = placement === "line";
  const badge = document.createElement("div");
  badge.className = [
    "berater-result-shipping-badge hidden relative inline-flex max-w-full items-center gap-2.5 rounded-full h-[24px] pl-[30px] pr-[18px] text-xs font-medium tracking-[0.01em]",
    isLine ? "bg-beige text-black mt-auto" : "bg-forestgreen text-white mt-2",
  ].join(" ");
  badge.dataset.beraterShippingBadge = "";
  badge.innerHTML = `${shippingBadgeSvg(isLine ? "text-black" : "text-white")}<span data-berater-shipping-badge-text></span>`;
  return badge;
};

interface CardSelectionState {
  items: ResolvedItem[];
  flowHandle: string;
  debug: boolean;
  relaxed: boolean;
  updateTotals: (country?: string) => void;
}

const cardSelectionState = new WeakMap<HTMLElement, CardSelectionState>();

const selectionSubtotalCents = (items: ResolvedItem[]): number =>
  items.reduce((sum, item) => sum + item.variant.price, 0);

const selectionWeightKg = (items: ResolvedItem[]): number =>
  items.reduce((sum, item) => sum + (item.variant.weight ?? 0), 0) / 1000;

const LINE_REMOVE_SVG = `<svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M10.1 20.2C15.68 20.2 20.2 15.68 20.2 10.1C20.2 4.52 15.68 0 10.1 0C4.52 0 0 4.52 0 10.1C0 15.68 4.52 20.2 10.1 20.2Z" fill="#F2F2F2"/><path d="M5.58984 10.0996H14.6098" stroke="#808080" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

const createLineRemoveSpacer = (): HTMLDivElement => {
  const spacer = document.createElement("div");
  spacer.className = "absolute bottom-0 right-2 size-[21px] shrink-0 pointer-events-none";
  spacer.setAttribute("aria-hidden", "true");
  return spacer;
};

const createLineRemove = (item: ResolvedItem, card: HTMLElement): HTMLButtonElement => {
  const removeLabel = card.dataset.removeLine || "Remove from selection";

  const button = document.createElement("button");
  button.type = "button";
  button.className =
    "absolute bottom-0 right-2 z-10 shrink-0 rounded-full p-0 leading-none transition-colors hover:bg-black/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-forestgreen";
  button.dataset.beraterLineRemove = String(item.variant.id);
  button.setAttribute("aria-label", `${removeLabel}: ${item.product.title}`);
  button.innerHTML = LINE_REMOVE_SVG;

  button.addEventListener("click", (event) => {
    event.preventDefault();
    event.stopPropagation();
    removeSelectionItem(card, item.variant.id);
  });

  return button;
};

const removeSelectionItem = (card: HTMLElement, variantId: number): void => {
  const state = cardSelectionState.get(card);
  const setBox = card.querySelector<HTMLElement>("[data-berater-card-set]");
  if (!state || !setBox) return;

  const idx = state.items.findIndex((item) => item.variant.id === variantId);
  if (idx <= 0) return;

  const removed = state.items[idx];
  state.items.splice(idx, 1);
  publishBeraterItemRemoved(state.flowHandle, state.debug, removed, state.items);
  renderSelectionLines(setBox, card, state);
  syncSelectionChrome(card, state);
  state.updateTotals();
};

interface AddToCartContext {
  flowHandle: string;
  debug: boolean;
  relaxed: boolean;
  atcType: "single" | "set";
}

const addItemsToCart = async (items: ResolvedItem[], context: AddToCartContext): Promise<boolean> => {
  try {
    const response = await fetch(`${(window as any).Shopify?.routes?.root ?? "/"}cart/add.js`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        items: items.map((item, index) => ({
          id: item.variant.id,
          quantity: 1,
          properties: beraterCartLineProperties(
            context.flowHandle,
            context.atcType,
            context.relaxed,
            index === 0 ? "main" : "accessory",
          ),
        })),
      }),
    });
    if (!response.ok) throw new Error(`cart/add ${response.status}`);
    publishBeraterAddToCart(context.flowHandle, context.debug, context.atcType, context.relaxed, items);
    window.openCartDrawerGlobal?.();
    return true;
  } catch (error) {
    console.error("[ek-berater] add to cart failed:", error);
    return false;
  }
};

const setLine = (
  item: ResolvedItem,
  card: HTMLElement,
  options: {
    lineRemoveLayout?: "button" | "spacer";
    padY?: boolean;
    singleProduct?: boolean;
    flowHandle?: string;
    debug?: boolean;
  } = {},
): HTMLElement => {
  const imageSrc = resolveVariantImageSrc(item.product, item.variant);
  const useGridLink = Boolean(imageSrc);
  const singleProduct = options.singleProduct ?? false;

  const row = document.createElement("div");
  row.className = [
    "berater-result-line flex gap-3 last:border-b-0",
    useGridLink ? "items-stretch" : "items-start",
    options.padY !== false ? "border-b border-separatorgray py-3" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const link = document.createElement("a");
  link.href = productPdpHref(item.product, item.variant.id);
  link.className = [
    "flex-1 min-w-0 gap-[22px] text-inherit no-underline rounded-sm transition-colors  focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-forestgreen",
    useGridLink ? "grid grid-cols-[96px_1fr]" : "flex items-start lg:items-center",
  ].join(" ");
  link.setAttribute("aria-label", item.product.title);
  if (options.flowHandle) {
    bindPdpClick(link, options.flowHandle, Boolean(options.debug), "gallery", item.product.handle, item.variant.id);
  }

  if (imageSrc) {
    const img = document.createElement("img");
    img.src = imageWithWidth(imageSrc, 192);
    img.alt = item.product.title;
    img.loading = "lazy";
    img.className = "w-[96px] h-[116px] object-cover shrink-0 bg-beige";
    link.appendChild(img);
  }

  const info = document.createElement("div");
  info.className = [
    useGridLink ? "flex min-w-0 h-full flex-col items-start" : "flex-1 min-w-0 flex flex-col items-start min-h-0",
    options.lineRemoveLayout ? "relative" : "",
  ]
    .filter(Boolean)
    .join(" ");
  const title = document.createElement("p");
  title.className = singleProduct
    ? "text-sm lg:text-base font-semibold tracking-[0.01em] leading-snug"
    : "text-sm lg:text-base font-semibold tracking-[0.01em] leading-snug lg:font-semibold";
  title.innerHTML = formatBeraterInlineText(item.product.title);
  info.appendChild(title);
  if (item.detail) {
    const detail = document.createElement("p");
    detail.className = singleProduct ? "text-sm lg:text-base text-black mt-1" : "text-sm text-black mt-1 lg:text-base lg:text-black";
    detail.textContent = item.detail;
    info.appendChild(detail);
  }

  const soldOut = !item.variant.available;
  if (soldOut) {
    row.classList.add("opacity-70");
    const soldOutEl = document.createElement("p");
    soldOutEl.className = "text-sm text-secondary mt-1";
    soldOutEl.textContent = window.variantStrings?.soldOut ?? "Ausverkauft";
    info.appendChild(soldOutEl);
  }

  const hasDiscount = Boolean(item.variant.compare_at_price && item.variant.compare_at_price > item.variant.price);
  const prices = document.createElement("p");
  prices.className = "text-sm mt-auto mb-0";
  const price = document.createElement("span");
  price.className = "font-normal text-black lg:text-base";
  price.textContent = formatPrice(item.variant.price / 100);
  prices.appendChild(price);
  if (hasDiscount) {
    const compare = document.createElement("s");
    compare.className = "text-secondary ml-2 font-normal";
    compare.textContent = formatPrice(item.variant.compare_at_price! / 100);
    prices.appendChild(compare);
  }
  info.appendChild(prices);
  if (options.lineRemoveLayout === "button") {
    info.appendChild(createLineRemove(item, card));
  } else if (options.lineRemoveLayout === "spacer") {
    info.appendChild(createLineRemoveSpacer());
  }
  link.appendChild(info);
  row.appendChild(link);

  return row;
};

const buildShippingBadgeText = (card: HTMLElement, country: string, cost: number): string => {
  if (country === "DE" && cost === 0) {
    return card.dataset.shippingLabelDe || "kostenloser Versand in DE";
  }
  if (cost === 0) {
    return card.dataset.shippingLabelFree || "Versandkostenfrei";
  }
  return `Versand nach ${country} · ${formatPrice(cost)}`;
};

const updateMobileShippingBadge = (card: HTMLElement, country: string, weightKg: number): void => {
  const badge = card.querySelector<HTMLElement>("[data-berater-shipping-badge]");
  const badgeText = card.querySelector<HTMLElement>("[data-berater-shipping-badge-text]");
  if (!badge || !badgeText) return;

  const cost = calculateShippingCost(country, weightKg);
  badgeText.textContent = buildShippingBadgeText(card, country, cost);
  badge.classList.remove("hidden");
  badge.classList.add("inline-flex");
};

const mountMobileShippingBadge = (card: HTMLElement, multiItem: boolean): void => {
  for (const orphan of card.querySelectorAll<HTMLElement>("[data-berater-shipping-badge]")) {
    orphan.remove();
  }

  const summaryWrap = card.querySelector<HTMLElement>("[data-berater-mobile-shipping-wrap]");
  if (multiItem) {
    if (!summaryWrap) return;
    summaryWrap.classList.remove("hidden");
    summaryWrap.classList.add("flex", "justify-start");
    summaryWrap.appendChild(createShippingBadge("summary"));
    return;
  }

  summaryWrap?.classList.add("hidden");
  summaryWrap?.classList.remove("flex", "justify-start");

  const lineInfo =
    card.querySelector<HTMLElement>("[data-berater-card-set] .berater-result-line a > div.flex.min-w-0") ??
    card.querySelector<HTMLElement>("[data-berater-card-set] .berater-result-line a > div.flex-1.min-w-0");
  lineInfo?.appendChild(createShippingBadge("line"));
};

const renderSelectionLines = (setBox: HTMLElement, card: HTMLElement, state: CardSelectionState): void => {
  const multiItem = state.items.length > 1;
  setBox.innerHTML = "";
  for (let i = 0; i < state.items.length; i += 1) {
    setBox.appendChild(
      setLine(state.items[i], card, {
        lineRemoveLayout: multiItem ? (i > 0 ? "button" : "spacer") : undefined,
        padY: multiItem,
        singleProduct: !multiItem,
        flowHandle: state.flowHandle,
        debug: state.debug,
      }),
    );
  }
};

const syncSelectionChrome = (card: HTMLElement, state: CardSelectionState): void => {
  const items = state.items;
  const multiItem = items.length > 1;
  const setTitle = card.querySelector<HTMLElement>("[data-berater-set-title]");
  const setTitleMobile = card.querySelector<HTMLElement>("[data-berater-set-title-mobile]");
  const atcButton = card.querySelector<HTMLButtonElement>("[data-berater-card-atc]");
  const desktopSummary = card.querySelector<HTMLElement>("[data-berater-card-summary]");

  mountMobileShippingBadge(card, multiItem);
  desktopSummary?.classList.toggle("hidden", !multiItem);

  if (atcButton) {
    atcButton.classList.toggle("lg:mt-5", multiItem);
    atcButton.classList.toggle("lg:mt-[26px]", !multiItem);
    atcButton.textContent = multiItem
      ? card.dataset.atcSet || "Set in den Warenkorb"
      : card.dataset.atcSingle || window.variantStrings?.addToCart || "In den Warenkorb";
  }

  if (setTitle) {
    setTitle.textContent =
      items.length === 1 ? card.dataset.setTitleSingle || setTitle.textContent : card.dataset.setTitleSet || setTitle.textContent;
  }
  if (setTitleMobile) {
    setTitleMobile.textContent = setTitle?.textContent ?? setTitleMobile.textContent;
  }
  setTitleMobile?.closest("p")?.classList.toggle("hidden", !multiItem);
  setTitle?.closest("p")?.classList.toggle("lg:flex", multiItem);
  syncAtcAvailability(card, state);
};

const syncAtcAvailability = (card: HTMLElement, state: CardSelectionState): void => {
  const atcButton = card.querySelector<HTMLButtonElement>("[data-berater-card-atc]");
  if (!atcButton || atcButton.classList.contains("pointer-events-none")) return;

  const hasUnavailable = state.items.some((item) => !item.variant.available);
  atcButton.disabled = hasUnavailable;
  atcButton.classList.toggle("button-berry", !hasUnavailable);
  atcButton.classList.toggle("button-gray-disabled", hasUnavailable);
  atcButton.classList.toggle("pointer-events-none", hasUnavailable);
  atcButton.classList.toggle("cursor-not-allowed", hasUnavailable);
};

const initSelectionTotals = (card: HTMLElement, state: CardSelectionState): void => {
  const select = card.querySelector<HTMLSelectElement>("[data-berater-shipping-select]");
  const shippingPrice = card.querySelector<HTMLElement>("[data-berater-shipping-price]");
  const totalEl = card.querySelector<HTMLElement>("[data-berater-card-total]");

  const applyTotal = (country: string): void => {
    const subtotalCents = selectionSubtotalCents(state.items);
    const weightKg = selectionWeightKg(state.items);
    const cost = calculateShippingCost(country, weightKg);
    if (totalEl) totalEl.textContent = formatPrice(subtotalCents / 100 + cost);
    updateMobileShippingBadge(card, country, weightKg);
  };

  if (select && shippingPrice) {
    populateCountrySelect(select);

    const updateDesktop = (): void => {
      const country = select.value;
      const cost = calculateShippingCost(country, selectionWeightKg(state.items));
      applyTotal(country);
      if (cost === 0 && country === "DE") {
        shippingPrice.textContent = card.dataset.shippingLabelDe || "Versandkostenfrei in DE";
        shippingPrice.classList.remove("hidden");
        select.classList.add("hidden");
      } else if (cost === 0) {
        shippingPrice.textContent = card.dataset.shippingLabelFree || "Versandkostenfrei";
        shippingPrice.classList.remove("hidden");
        select.classList.remove("hidden");
      } else {
        shippingPrice.textContent = formatPrice(cost);
        shippingPrice.classList.remove("hidden");
        select.classList.remove("hidden");
      }
    };

    select.addEventListener("change", () => {
      persistCountry(select.value);
      updateDesktop();
    });

    state.updateTotals = (country?: string) => {
      if (country) applyTotal(country);
      else updateDesktop();
    };
    state.updateTotals();
    return;
  }

  state.updateTotals = (country?: string) => applyTotal(country ?? getInitialCountry());
  state.updateTotals();
};

const bindPdpClick = (
  link: HTMLAnchorElement,
  flowHandle: string,
  debug: boolean,
  clickTarget: "winner" | "gallery" | "details_fallback",
  productHandle: string,
  variantId?: number,
): void => {
  link.addEventListener("click", () => {
    publishBeraterPdpClick(flowHandle, debug, { clickTarget, productHandle, variantId });
  });
};

const bindAtc = (card: HTMLElement): void => {
  const button = card.querySelector<HTMLButtonElement>("[data-berater-card-atc]");
  const errorEl = card.querySelector<HTMLElement>("[data-berater-card-atc-error]");
  if (!button || button.dataset.beraterAtcBound === "true") return;

  button.dataset.beraterAtcBound = "true";
  button.addEventListener("click", async () => {
    const state = cardSelectionState.get(card);
    if (!state || button.disabled) return;

    button.disabled = true;
    button.classList.add("opacity-60");
    errorEl?.classList.add("hidden");

    const atcType = state.items.length > 1 ? "set" : "single";
    const ok = await addItemsToCart(state.items, {
      flowHandle: state.flowHandle,
      debug: state.debug,
      relaxed: state.relaxed,
      atcType,
    });
    if (!ok) errorEl?.classList.remove("hidden");

    button.disabled = false;
    button.classList.remove("opacity-60");
  });
};

const initWinnerTrustpilotStars = (card: HTMLElement, productJs: ShopifyJsProduct): void => {
  const starsEl = card.querySelector<HTMLElement>("[data-berater-card-stars]");
  if (!starsEl) return;

  const skus = productJs.variants.map((variant) => variant.sku ?? "").filter(Boolean);

  void renderTrustpilotStars(starsEl, skus, () => {
    openTrustpilotDrawer(productJs.handle, productJs.title, skus);
  });
};

const setupWinnerCard = async (
  card: HTMLElement,
  productJs: ShopifyJsProduct,
  data: BeraterData,
  answers: BeraterAnswer[],
  recommendation: BeraterRecommendation,
): Promise<ResolvedItem[] | null> => {
  initWinnerTrustpilotStars(card, productJs);

  try {
    initGallerySwiper(card);
  } catch (error) {
    console.error("[ek-berater] gallery swiper init failed:", error);
  }

  const desired = buildDesiredSelection(data, answers);
  const main = resolveMainVariant(productJs, desired);

  const subtitleEl = card.querySelector<HTMLElement>("[data-berater-card-subtitle]");
  const purchaseWrap = card.querySelector<HTMLElement>("[data-berater-card-purchase]");
  const setInner = card.querySelector<HTMLElement>("[data-berater-card-set-inner]");
  const setBox = card.querySelector<HTMLElement>("[data-berater-card-set]");
  const atcButton = card.querySelector<HTMLButtonElement>("[data-berater-card-atc]");
  const configureLink = card.querySelector<HTMLAnchorElement>("[data-berater-card-configure]");
  const pdpLink = card.querySelector<HTMLAnchorElement>("[data-berater-card-pdp]");
  const soldoutNote = card.querySelector<HTMLElement>("[data-berater-card-soldout]");
  const desktopSummary = card.querySelector<HTMLElement>("[data-berater-card-summary]");

  const pdpHref = (variantId?: number): string => productPdpHref(productJs, variantId);

  const syncPdpLinks = (href: string) => {
    if (pdpLink) pdpLink.href = href;
    const panel = card.closest<HTMLElement>('[data-berater-panel="result"]');
    const footerPdp = panel?.querySelector<HTMLAnchorElement>("[data-berater-result-pdp]");
    if (footerPdp) footerPdp.href = href;
  };

  syncPdpLinks(pdpHref(main.item?.variant.id));

  if (pdpLink) {
    bindPdpClick(pdpLink, data.flowHandle, Boolean(data.debug), "winner", productJs.handle, main.item?.variant.id);
  }
  const resultPanel = card.closest<HTMLElement>('[data-berater-panel="result"]');
  const footerPdp = resultPanel?.querySelector<HTMLAnchorElement>("[data-berater-result-pdp]");
  if (footerPdp) {
    bindPdpClick(footerPdp, data.flowHandle, Boolean(data.debug), "winner", productJs.handle, main.item?.variant.id);
  }

  // Multi-config products (premium Familienbett): honest "configure on PDP" instead of ATC
  if (main.status === "configure" || main.status === "none") {
    setInner?.classList.add("hidden");
    desktopSummary?.classList.add("hidden");
    atcButton?.classList.add("hidden");
    purchaseWrap?.classList.remove("hidden");
    if (configureLink) {
      configureLink.href = pdpHref();
      configureLink.classList.remove("hidden");
      bindPdpClick(configureLink, data.flowHandle, Boolean(data.debug), "details_fallback", productJs.handle);
    }
    return main.item ? [main.item] : null;
  }

  const mainItem = main.item;
  if (!mainItem) return null;

  patchGalleryHeroImage(card, productJs, mainItem.variant);

  const sizedDesired = desiredSelectionWithMainVariant(productJs, desired, mainItem.variant);

  if (subtitleEl) {
    // "Jugendbett Plano | Eiche" → tail "Eiche" carries the wood, variant carries color/size
    const titleTail = productJs.title.includes("|") ? productJs.title.split("|").slice(1).join(" ").trim() : "";
    const subtitle = [titleTail, buildSubtitle(productJs, mainItem.variant)].filter(Boolean).join("  |  ");
    if (subtitle) subtitleEl.innerHTML = formatBeraterInlineText(subtitle);
  }

  // Accessories: addon variants resolve inside the same product, separate products are fetched
  const items: ResolvedItem[] = [mainItem];
  for (const accessory of sizedDesired.accessories) {
    const mode = normalizeAccessoryMode(String(accessory.mode));
    if (mode === "addon_variant" && !isBundleTokenInVariant(productJs, mainItem.variant, accessory.token)) {
      const item = resolveAccessoryItem(productJs, accessory, sizedDesired, { allowSoldOut: true });
      if (item) items.push(item);
    }
    if (mode === "bundle_variant" && !isBundleTokenInVariant(productJs, mainItem.variant, accessory.token)) {
      const item = resolveAccessoryItem(productJs, { ...accessory, mode: "addon_variant" }, sizedDesired, { allowSoldOut: true });
      if (item) items.push(item);
    }
    if (mode === "separate_product" && accessory.linkedProductHandle) {
      const linked = await fetchProductJs(accessory.linkedProductHandle);
      if (linked) {
        const item = resolveAccessoryItem(linked, accessory, sizedDesired, { allowSoldOut: true });
        if (item) {
          items.push(item);
        } else if (data.debug) {
          console.warn("[ek-berater] separate product not resolved:", accessory);
        }
      } else if (data.debug) {
        console.warn("[ek-berater] separate product fetch failed:", accessory.linkedProductHandle);
      }
    }
  }

  const state: CardSelectionState = {
    items,
    flowHandle: data.flowHandle,
    debug: Boolean(data.debug),
    relaxed: recommendation.relaxed,
    updateTotals: () => {},
  };
  cardSelectionState.set(card, state);

  if (setBox) {
    renderSelectionLines(setBox, card, state);
  }
  syncSelectionChrome(card, state);
  initSelectionTotals(card, state);

  if (main.status === "soldout") {
    if (atcButton) {
      atcButton.disabled = true;
      atcButton.textContent = window.variantStrings?.soldOut ?? "Ausverkauft";
      atcButton.classList.remove("button-berry");
      atcButton.classList.add("button-gray-disabled", "pointer-events-none", "cursor-not-allowed");
    }
    soldoutNote?.classList.remove("hidden");
    return items;
  }

  bindAtc(card);

  return items;
};

/**
 * Renders the full result panel: winner card via Section Rendering API,
 * variant resolution + set box + shipping + ATC.
 * Idempotent per answers-set (skips when already rendered for the same answers).
 */
export const renderResult = async (root: HTMLElement, data: BeraterData, answers: BeraterAnswer[]): Promise<void> => {
  const panel = root.querySelector<HTMLElement>('[data-berater-panel="result"]');
  if (!panel) return;

  const renderKey = `${data.flowHandle}:${JSON.stringify(answers)}`;
  const winnerSlot = panel.querySelector<HTMLElement>("[data-berater-result-winner]");
  const fallbackEl = panel.querySelector<HTMLElement>("[data-berater-result-fallback]");
  const emptyEl = panel.querySelector<HTMLElement>("[data-berater-result-empty]");

  if (panel.dataset.renderedFor === renderKey) {
    const hasWinnerCard = Boolean(winnerSlot?.querySelector("[data-berater-card], .button-black"));
    const hasEmptyState = Boolean(emptyEl && !emptyEl.classList.contains("hidden"));
    if (hasWinnerCard || hasEmptyState) return;
    delete panel.dataset.renderedFor;
  }

  const recommendations = getRecommendations(data, answers);

  if (!recommendations.length) {
    winnerSlot?.classList.add("hidden");
    fallbackEl?.classList.add("hidden");
    emptyEl?.classList.remove("hidden");
    panel.dataset.renderedFor = renderKey;
    publishBeraterEmptyResult(data.flowHandle, Boolean(data.debug), renderKey);
    return;
  }

  emptyEl?.classList.add("hidden");
  winnerSlot?.classList.remove("hidden");

  if (fallbackEl) {
    const winnerRec = recommendations[0];
    if (winnerRec.relaxed) {
      const dropped = winnerRec.droppedCriteria.map((key) => DROPPED_LABELS[key] ?? key).join(", ");
      const base = data.resultFallbackText || fallbackEl.dataset.defaultText || "";
      fallbackEl.textContent = dropped ? `${base} (Nicht berücksichtigt: ${dropped})` : base;
      fallbackEl.classList.remove("hidden");
    } else {
      fallbackEl.classList.add("hidden");
    }
  }

  const winner = recommendations[0];
  const winnerHandle = winner.handle;
  const productJs = await fetchProductJs(winnerHandle);

  // Resolve variant first so SRA can render the CL gallery (?variant= sets product.selected_variant in Liquid).
  const desired = buildDesiredSelection(data, answers);
  const main = productJs ? resolveMainVariant(productJs, desired) : null;
  const variantQuery = main?.item?.variant.id ? `&variant=${main.item.variant.id}` : "";
  const cardHtml = productJs ? await fetchSectionHtml(winnerHandle, WINNER_CARD_SECTION, variantQuery) : null;

  let resolvedItems: ResolvedItem[] | null = null;

  if (winnerSlot) {
    if (cardHtml && productJs) {
      winnerSlot.innerHTML = cardHtml;
      const card = winnerSlot.querySelector<HTMLElement>("[data-berater-card]");
      if (card) {
        try {
          resolvedItems = await setupWinnerCard(card, productJs, data, answers, winner);
        } catch (error) {
          console.error("[ek-berater] setupWinnerCard failed:", error);
        }
      }
    } else {
      // Network/render failure: honest minimal fallback with a PDP link from inline data
      const candidate = data.candidates.find((item) => item.handle === winnerHandle);
      winnerSlot.innerHTML = "";
      const box = document.createElement("div");
      box.className = "rounded-lg bg-white/60 border border-secondary/30 p-6 text-center";
      const title = document.createElement("p");
      title.className = "font-medium mb-3";
      title.textContent = candidate?.title ?? winnerHandle;
      const link = document.createElement("a");
      link.className = "button-black inline-block !px-6";
      link.href = `${candidate?.url ?? `/products/${winnerHandle}`}?ref=berater`;
      link.textContent = "Details ansehen";
      bindPdpClick(link, data.flowHandle, Boolean(data.debug), "details_fallback", winnerHandle);
      box.appendChild(title);
      box.appendChild(link);
      winnerSlot.appendChild(box);
    }
  }

  if (resolvedItems?.length) {
    publishBeraterResultViewed(
      data.flowHandle,
      Boolean(data.debug),
      renderKey,
      winner,
      resolvedItems,
      0,
      answers,
    );
  }

  panel.dataset.renderedFor = renderKey;
};
